import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RISK_META, RISK_FALLBACK, formatLastSeen } from "@/lib/risk";
import MetricCard from "@/components/MetricCard";
import { RiskBadge, TierBadge, PocChip, PocChipPair, UsersCell } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import RulesInfoDialog from "@/components/RulesInfoDialog";
import ExportButton from "@/components/ExportButton";
import { FIRM_BASE_COLUMNS } from "@/lib/exportColumns";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  CaretRight,
  CaretLeft,
  PhoneCall,
  Note,
} from "@phosphor-icons/react";
import FirmCallLogsDialog from "@/components/FirmCallLogsDialog";
import NotesDialog from "@/components/NotesDialog";

const PAGE_SIZE = 50;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPoc = user?.role === "poc";
  const [searchParams, setSearchParams] = useSearchParams();

  // State driven from URL for filter preservation across drill-down
  const filters = {
    search: searchParams.get("search") || "",
    tier: searchParams.get("tier") || "all",
    poc: searchParams.get("poc") || "all",
    activation: searchParams.get("activation") || "all",
    activation_from: searchParams.get("activation_from") || "",
    activation_to: searchParams.get("activation_to") || "",
    risk: searchParams.get("risk") || "all",
    page: parseInt(searchParams.get("page") || "1", 10),
  };

  const [searchInput, setSearchInput] = useState(filters.search);
  const [stats, setStats] = useState({ total: 0, counts: {} });
  const [firms, setFirms] = useState([]);
  const [totalFirms, setTotalFirms] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pocOptions, setPocOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);
  // Quick-action dialogs triggered from the row icons (between POC and >).
  // Holds the firm doc (or null) — both dialogs accept the same `firm` prop.
  const [callLogsFirm, setCallLogsFirm] = useState(null);
  const [notesFirm, setNotesFirm] = useState(null);

  const setFilter = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (!value || value === "all" || value === "") next.delete(key);
      else next.set(key, value);
      if (key !== "page") next.delete("page");
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  // Fetch options once
  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs);
      setTierOptions(r.data.tiers);
    });
  }, []);

  // Debounce search input -> URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== filters.search) setFilter("search", searchInput);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Sync local search field if filter changes externally
  useEffect(() => {
    setSearchInput(filters.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const isCustom = filters.activation === "custom";
    const params = {
      search: filters.search || undefined,
      tier: filters.tier !== "all" ? filters.tier : undefined,
      poc: filters.poc !== "all" ? filters.poc : undefined,
      risk: filters.risk !== "all" ? filters.risk : undefined,
      activation_window:
        filters.activation !== "all" && !isCustom ? filters.activation : undefined,
      activation_from: isCustom && filters.activation_from ? filters.activation_from : undefined,
      activation_to: isCustom && filters.activation_to ? filters.activation_to : undefined,
      page: filters.page,
      page_size: PAGE_SIZE,
    };
    try {
      const [firmsRes, statsRes] = await Promise.all([
        api.get("/firms", { params }),
        api.get("/firms/stats", {
          params: {
            search: params.search,
            tier: params.tier,
            poc: params.poc,
            activation_window: params.activation_window,
            activation_from: params.activation_from,
            activation_to: params.activation_to,
          },
        }),
      ]);
      setFirms(firmsRes.data.firms);
      setTotalFirms(firmsRes.data.total);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  }, [
    filters.search,
    filters.tier,
    filters.poc,
    filters.risk,
    filters.activation,
    filters.activation_from,
    filters.activation_to,
    filters.page,
  ]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const totalPages = Math.max(1, Math.ceil(totalFirms / PAGE_SIZE));

  const riskTabs = useMemo(
    () => [
      { key: "all", label: "All", count: stats.total || 0 },
      { key: "Active", label: "Active", count: stats.counts.Active || 0 },
      { key: "At Risk", label: "At Risk", count: stats.counts["At Risk"] || 0 },
      { key: "Inactive", label: "Inactive", count: stats.counts.Inactive || 0 },
      {
        key: "Potential Churn",
        label: "Potential Churn",
        count: stats.counts["Potential Churn"] || 0,
      },
    ],
    [stats]
  );

  const openFirm = (subdomain) => {
    navigate(`/firms/${subdomain}${firmFromQS(location)}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900">
            Customer Success Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
            <span>Live health for {stats.total || 0} firms.</span>
            <RulesInfoDialog view="dashboard" label="the dashboard" />
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center" data-testid="filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            placeholder="Search firm, subdomain, city, POC…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
            data-testid="filter-search"
          />
        </div>
        <Select value={filters.tier} onValueChange={(v) => setFilter("tier", v)}>
          <SelectTrigger className="w-[150px]" data-testid="filter-tier">
            <SelectValue placeholder="All tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {tierOptions.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.poc} onValueChange={(v) => setFilter("poc", v)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-poc">
            <SelectValue placeholder="All POCs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All POCs</SelectItem>
            {pocOptions.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.activation}
          onValueChange={(v) => {
            // Switching away from Custom clears the from/to dates so the
            // backend doesn't keep narrowing the result by stale bounds.
            if (v !== "custom") {
              const next = new URLSearchParams(searchParams);
              next.delete("activation_from");
              next.delete("activation_to");
              if (!v || v === "all") next.delete("activation");
              else next.set("activation", v);
              next.delete("page");
              setSearchParams(next, { replace: false });
            } else {
              setFilter("activation", "custom");
            }
          }}
        >
          <SelectTrigger className="w-[170px]" data-testid="filter-activation">
            <SelectValue placeholder="Activation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="30d">Past 30 days</SelectItem>
            <SelectItem value="60d">Past 60 days</SelectItem>
            <SelectItem value="90d">Past 90 days</SelectItem>
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>
        {filters.activation === "custom" && (
          <div className="flex items-center gap-2" data-testid="filter-activation-custom">
            <Input
              type="date"
              value={filters.activation_from}
              onChange={(e) => setFilter("activation_from", e.target.value)}
              max={filters.activation_to || undefined}
              className="w-[150px]"
              data-testid="filter-activation-from"
            />
            <span className="text-gray-400 text-sm">to</span>
            <Input
              type="date"
              value={filters.activation_to}
              onChange={(e) => setFilter("activation_to", e.target.value)}
              min={filters.activation_from || undefined}
              className="w-[150px]"
              data-testid="filter-activation-to"
            />
          </div>
        )}
        {(filters.search ||
          filters.tier !== "all" ||
          filters.poc !== "all" ||
          filters.activation !== "all" ||
          filters.risk !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchParams({}, { replace: true });
              setSearchInput("");
            }}
            data-testid="clear-filters-btn"
            className="text-gray-600"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="metric-cards">
        <MetricCard
          label="Total Live"
          value={stats.total || 0}
          isTotal
          active={filters.risk === "all"}
          onClick={() => setFilter("risk", "all")}
        />
        {["Active", "At Risk", "Inactive", "Potential Churn"].map((r) => (
          <MetricCard
            key={r}
            label={r}
            value={stats.counts[r] || 0}
            riskKey={r}
            active={filters.risk === r}
            onClick={() => setFilter("risk", filters.risk === r ? "all" : r)}
          />
        ))}
      </div>

      {/* Risk tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 justify-between" data-testid="risk-tabs">
        <div className="flex items-center gap-1">
          {riskTabs.map((tab) => {
            const active = filters.risk === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter("risk", tab.key)}
                className={`pb-2.5 px-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  active
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`risk-tab-${tab.key.replace(/\s/g, "-")}`}
              >
                {tab.label}
                <span
                  className={`ml-2 inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    active ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="pb-2">
          <ExportButton
            filename={`dashboard-${filters.risk === "all" ? "all" : filters.risk.replace(/\s+/g, "-").toLowerCase()}`}
            sheetName="Firms"
            columns={FIRM_BASE_COLUMNS}
            fetchRows={async () => {
              const isCustom = filters.activation === "custom";
              const params = {
                search: filters.search || undefined,
                tier: filters.tier !== "all" ? filters.tier : undefined,
                poc: filters.poc !== "all" ? filters.poc : undefined,
                risk: filters.risk !== "all" ? filters.risk : undefined,
                activation_window:
                  filters.activation !== "all" && !isCustom ? filters.activation : undefined,
                activation_from:
                  isCustom && filters.activation_from ? filters.activation_from : undefined,
                activation_to:
                  isCustom && filters.activation_to ? filters.activation_to : undefined,
                page: 1,
                page_size: 10000,
              };
              const r = await api.get("/firms", { params });
              return r.data.firms || [];
            }}
            testId="export-dashboard"
          />
        </div>
      </div>

      <div data-testid="firm-table-wrapper">
        {/* Firm table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="firm-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left">
                  <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                    Firm
                  </th>
                  <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                    Users
                  </th>
                  <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                    Risk
                  </th>
                  <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500 whitespace-nowrap min-w-[140px]">
                    Last seen
                  </th>
                  <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                    POC
                  </th>
                  <th className="px-2 py-3 w-20"></th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                      Loading firms…
                    </td>
                  </tr>
                )}
                {!loading && firms.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                      No firms match the current filters.
                    </td>
                  </tr>
                )}
                {!loading &&
                  firms.map((f) => {
                    const meta = RISK_META[f.risk] || RISK_FALLBACK;
                    return (
                      <tr
                        key={f.subdomain}
                        onClick={() => openFirm(f.subdomain)}
                        className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${meta.leftClass}`}
                        data-testid={`firm-row-${f.subdomain}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{f.firm_name}</div>
                          <FirmSubmeta firm={f} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <UsersCell value={f.num_users} />
                        </td>
                        <td className="px-4 py-3">
                          <TierBadge tier={f.tier} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <RiskBadge risk={f.risk} />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {formatLastSeen(f.last_seen, f.days_since_seen)}
                        </td>
                        <td className="px-4 py-3">
                          <PocChipPair poc={f.poc} salesPoc={f.sales_poc} />
                        </td>
                        {/* Quick-actions: open per-firm Call Logs & Notes dialogs
                            without leaving the dashboard. stopPropagation keeps
                            the row's row-level "navigate to Firm 360" intact. */}
                        <td className="px-2 py-3 whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setCallLogsFirm(f); }}
                              title="Call logs"
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                              data-testid={`firm-row-calls-${f.subdomain}`}
                            >
                              <PhoneCall size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setNotesFirm(f); }}
                              title="Notes"
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                              data-testid={`firm-row-notes-${f.subdomain}`}
                            >
                              <Note size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <CaretRight size={16} className="text-gray-400" />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalFirms > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
              <div className="text-gray-500">
                Page {filters.page} of {totalPages} · {totalFirms} firms
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page <= 1}
                  onClick={() => setFilter("page", String(filters.page - 1))}
                  data-testid="pagination-prev"
                >
                  <CaretLeft size={14} className="mr-1" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilter("page", String(filters.page + 1))}
                  data-testid="pagination-next"
                >
                  Next
                  <CaretRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick-action dialogs — render once at the page level so any row can
          open them by setting state. Both dialogs accept the same `firm` shape. */}
      <FirmCallLogsDialog
        open={!!callLogsFirm}
        firm={callLogsFirm}
        onClose={() => setCallLogsFirm(null)}
      />
      <NotesDialog
        open={!!notesFirm}
        firm={notesFirm}
        onOpenChange={(v) => { if (!v) setNotesFirm(null); }}
      />
    </div>
  );
}
